import { useState } from "react";
import SEO from "@/components/SEO";
import { api } from "@/lib/api";
import { BRAND } from "@/brand";
import { toast } from "sonner";

const REASONS = [
  { title: "Payout & balance questions", body: "Missing payout, delayed status, verification questions." },
  { title: "Card rejections", body: "Rejected submission — we'll explain exactly why in writing." },
  { title: "Partnership or press", body: "Business inquiries, integrations, media requests." },
];

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Message sent! We'll reply within 6 hours.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <SEO title="Contact GC SPARK" description={`Reach the ${BRAND.name} team. Average reply time is under 6 hours on business days.`} />

      {/* Sign-up section style split (theme's marketing form layout) */}
      <section className="sign-up-section" data-testid="contact-hero">
        <div className="sign-up-container">
          <div className="sign-up-text-container">
            <div style={{ display: "inline-block", padding: "6px 14px", borderRadius: 90, background: "#0b0d17", color: "#fff", fontSize: 12, fontWeight: 700, letterSpacing: ".12em", textTransform: "uppercase", marginBottom: 22 }}>
              Contact us
            </div>
            <div className="sign-up-heading">
              <h1 className="h1 _700-bold">Real people answer <span style={{ color: "#346af8" }}>real quickly.</span></h1>
            </div>
            <div className="sign-up-para">
              <p className="main-body-text _500-medium">
                GC SPARK is owned and operated by {BRAND.ownerName}. For questions about a submitted
                gift card, payment status, verification, or account information, please contact our
                support team. Our team is available 24/7 via email and live chat.
              </p>
            </div>
            <div className="sign-up-list" style={{ marginTop: 30 }}>
              {REASONS.map((r) => (
                <div className="why-us-list-row" key={r.title} style={{ marginBottom: 14 }}>
                  <figure className="check-icon-wrapper">
                    <img src="/jumbo/images/checkamrk.svg" loading="lazy" alt="" className="check-icon" />
                  </figure>
                  <div>
                    <div style={{ fontWeight: 700, color: "#0b0d17", fontSize: 15 }}>{r.title}</div>
                    <div style={{ fontSize: 13.5, color: "#6c6376", marginTop: 2 }}>{r.body}</div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 32, padding: 20, background: "#fff", border: "1px solid #eaeaf0", borderRadius: 16, display: "inline-block" }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".14em", textTransform: "uppercase", color: "#9a93a6" }}>Email directly</div>
              <a href={`mailto:${BRAND.supportEmail}`} style={{ display: "block", marginTop: 6, fontSize: 20, fontWeight: 700, color: "#0b0d17", textDecoration: "none" }} data-testid="contact-support-email">
                {BRAND.supportEmail}
              </a>
            </div>
          </div>

          <div className="sign-up-form-div-wrapper">
            <div className="sign-up-form-wrapper" style={{ maxWidth: 460 }}>
              {sent ? (
                <div data-testid="contact-success" style={{ padding: 40, textAlign: "center", background: "#fff", borderRadius: 20, border: "1px solid #eaeaf0" }}>
                  <div style={{ width: 60, height: 60, borderRadius: "50%", background: "#eaf0ff", display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 16 }}>
                    <img src="/jumbo/images/checkamrk.svg" alt="" style={{ width: 28 }} />
                  </div>
                  <h3 className="h4 _700-bold" style={{ marginBottom: 10 }}>Message received</h3>
                  <p style={{ color: "#6c6376", marginBottom: 20 }}>We'll reply to your email within 6 business hours.</p>
                  <button className="btn w-inline-block" onClick={() => setSent(false)}>
                    <div className="btn-text">Send another</div>
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="contact-form" style={{ background: "#fff", padding: 32, borderRadius: 24, border: "1px solid #eaeaf0", boxShadow: "0 24px 60px -30px rgba(41,45,85,.25)" }}>
                  <h3 className="h4 _700-bold" style={{ marginBottom: 22 }}>Send us a message</h3>
                  <Field label="Your name" testid="contact-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
                  <Field label="Email" type="email" testid="contact-email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
                  <Field label="Subject" testid="contact-subject" value={form.subject} onChange={(v) => setForm({ ...form, subject: v })} />
                  <div style={{ marginBottom: 18 }}>
                    <label style={labelStyle}>Message</label>
                    <textarea required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                      data-testid="contact-message" placeholder="Tell us what happened, or what you need to know" style={{ ...inputStyle, height: 130, paddingTop: 12, resize: "vertical", fontFamily: "inherit" }} />
                  </div>
                  <button type="submit" disabled={sending} data-testid="contact-submit" className="primary-button w-button" style={{ width: "100%", opacity: sending ? 0.6 : 1, border: "none", cursor: sending ? "wait" : "pointer" }}>
                    {sending ? "Sending…" : "Send message"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

const labelStyle = { display: "block", fontSize: 12, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "#0b0d17", marginBottom: 8 };
const inputStyle = { width: "100%", padding: "13px 16px", borderRadius: 12, border: "1px solid #d8dcec", background: "#fff", color: "#0b0d17", fontSize: 15, outline: "none" };

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={labelStyle}>{label}</label>
      <input type={type} required value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} placeholder="Type here" style={inputStyle} />
    </div>
  );
}
